import { IconX } from "@tabler/icons-react";
import { ReactNode, useState } from "react";
import {
  useFetchTicketDetails,
  useCloseTicket,
  useReopenTicket,
  useAddTicketComment,
  useUpdateTicket
} from "../../../apis/ticketManagementClientApis";
import moment from "moment";
import ButtonStyled from "../../../components/common/ButtonStyled";
import { usePermission } from "../../../hooks/usePermissionTabs";
import TextareaField from "../../../components/common/TextareaField";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";

type Props = {
  open: boolean;
  onClose: () => void;
  ticketId: string;
};

const InfoRow = ({ label, value }: { label: string; value: ReactNode }) => (
  <div className="flex justify-between items-center w-full">
    <div className="w-full">
      <h2 className="capitalize text-[#212121] text-sm font-medium">{label}</h2>
    </div>
    <div className="w-full text-end">
      {typeof value === "string" ? (
        <p className="text-[#1E293B] text-base">{value}</p>
      ) : (
        value
      )}
    </div>
  </div>
);

const ViewModal = ({ open, onClose, ticketId }: Props) => {
  const { data: ticket, isLoading } = useFetchTicketDetails(ticketId);
  const { mutate: closeTicket, isPending: closing } = useCloseTicket();
  const { mutate: reopenTicket, isPending: reopening } = useReopenTicket();
  const { mutate: addComment, isPending: addingComment } = useAddTicketComment();
  const { mutate: updateTicket, isPending: updating } = useUpdateTicket();
  
  const profile = useSelector((state: any) => state.auth?.profile);
  const { allowed: canUpdateTicket } = usePermission("tickets", "update");
  
  const { register, handleSubmit, reset, formState: { errors } } = useForm({
    defaultValues: { message: "" }
  });

  const [showCommentBox, setShowCommentBox] = useState(false);

  const [activeTab, setActiveTab] = useState<"COMMENTS" | "ACTIVITY">("COMMENTS");

  if (!open) return null;

  const onCommentSubmit = (data: { message: string }) => {
    addComment({ ticketId, data: { message: data.message, attachmentUrls: [] } }, {
      onSuccess: () => {
        reset();
        setShowCommentBox(false);
      }
    });
  };

  return (
    <div className="fixed top-0 left-0 w-full h-screen bg-black/60 z-50 flex items-center justify-center">
      <div className="bg-[#F7F6F2] rounded-2xl w-[655px] max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-4 py-4 border-b border-[#d2d2d2]">
          <h1 className="text-2xl">
            View Ticket {ticket ? `- ${ticket.ticketNumber}` : ""}
          </h1>
          <button
            onClick={onClose}
            className="bg-[#F2F2F7] rounded-full p-1 cursor-pointer"
          >
            <IconX size={20} />
          </button>
        </div>

        <div className="w-full overflow-y-auto p-4 space-y-4 flex-1">
          {isLoading ? (
            <div className="text-center py-4">Loading details...</div>
          ) : ticket ? (
            <>
              <InfoRow label="Subject" value={ticket.subject} />
              <InfoRow label="AWB Number" value={ticket.awbNumber || ""} />
              <InfoRow label="Category Type" value={ticket.type.replace("_", " ")} />
              <InfoRow label="Date" value={moment(ticket.createdAt).format("DD-MMM-YYYY, h:mm A")} />
              <div className="flex justify-between items-center w-full">
                <div className="w-full">
                  <h2 className="capitalize text-[#212121] text-sm font-medium">
                    Status
                  </h2>
                </div>

                <select
                  className={`rounded-full w-36 px-4 py-1 text-center font-medium focus:outline-none cursor-pointer transition-all ${
                    ticket.status === 'RESOLVED' || ticket.status === 'CLOSED' ? "bg-[#E6F7E8] border border-[#20BF55] text-[#212121]" :
                    ticket.status === 'OPEN' || ticket.status === 'REOPENED' ? "bg-[#FF4848]/10 border border-[#EC221F] text-[#FF4848]" :
                    "bg-[#FCB910]/10 border border-[#FCB910] text-[#FCB910]"
                  }`}
                  value={ticket.status}
                  onChange={(e) => updateTicket({ ticketId, data: { status: e.target.value } })}
                  disabled={updating}
                >
                  <option value="OPEN">OPEN</option>
                  <option value="IN_PROGRESS">IN PROGRESS</option>
                  <option value="ON_HOLD">ON HOLD</option>
                  <option value="RESOLVED">RESOLVED</option>
                  <option value="CLOSED">CLOSED</option>
                  <option value="REOPENED">REOPENED</option>
                </select>
              </div>
              <InfoRow label="Priority" value={ticket.priority} />
              <div className="mt-4 p-4 bg-white rounded-lg border border-gray-200">
                <h3 className="font-semibold mb-2">Description</h3>
                <p className="text-gray-700 whitespace-pre-wrap">{ticket.description}</p>
              </div>

              {ticket.attachmentUrls && ticket.attachmentUrls.length > 0 && (
                <div className="mt-4 p-4 bg-white rounded-lg border border-gray-200">
                  <h3 className="font-semibold mb-2">Attachments</h3>
                  <div className="flex flex-wrap gap-4 mt-2">
                    {ticket.attachmentUrls.map((url: string, index: number) => {
                      const cleanUrl = url.split("?")[0];
                      const isImage = cleanUrl.match(/\.(jpeg|jpg|gif|png|webp|avif)$/i) || url.startsWith('blob:');
                      let fileName = cleanUrl.substring(cleanUrl.lastIndexOf("/") + 1);
                      if (!fileName || url.startsWith('blob:')) {
                        fileName = `Attachment ${index + 1}`;
                      }
                      
                      return (
                        <div key={index} className="flex flex-col items-center gap-2">
                          {isImage ? (
                            <a href={url} target="_blank" rel="noopener noreferrer" className="block hover:opacity-80 transition-opacity border border-[#D2D2D2] rounded-xl overflow-hidden shadow-sm">
                              <img src={url} alt={fileName} className="w-24 h-24 object-cover" />
                            </a>
                          ) : (
                            <a 
                              href={url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="flex items-center gap-2 bg-[#F2F2F7] px-4 py-3 border border-[#D2D2D2] rounded-xl hover:bg-gray-200 transition-colors shadow-sm"
                            >
                              <span className="text-sm text-[#212121] truncate max-w-[150px] font-medium">{fileName}</span>
                            </a>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Tabs for Comments and Activity Logs */}
              <div className="mt-6 border-t pt-4">
                <div className="flex gap-4 mb-4">
                  <button 
                    onClick={() => setActiveTab("COMMENTS")}
                    className={`font-semibold text-lg pb-1 border-b-2 ${activeTab === "COMMENTS" ? "border-[#FBD037] text-black" : "border-transparent text-gray-500"}`}
                  >
                    Communication
                  </button>
                  <button 
                    onClick={() => setActiveTab("ACTIVITY")}
                    className={`font-semibold text-lg pb-1 border-b-2 ${activeTab === "ACTIVITY" ? "border-[#FBD037] text-black" : "border-transparent text-gray-500"}`}
                  >
                    Activity Log
                  </button>
                </div>
                
                {activeTab === "COMMENTS" ? (
                  <>
                    {ticket.comments && ticket.comments.length > 0 ? (
                      <div className="space-y-4 max-h-[300px] overflow-y-auto pr-2">
                        {ticket.comments.map((comment: any) => {
                          const isMerchant = comment.authorType === 'MERCHANT' || (comment.authorType === 'ADMIN' && profile?.type === 'client');
                          return (
                            <div key={comment.id} className={`p-3 rounded-lg ${isMerchant || comment.authorType === 'CUSTOMER' ? 'bg-blue-50 ml-auto border border-blue-100' : 'bg-gray-100 mr-auto border border-gray-200'} w-[85%]`}>
                              <div className="flex justify-between items-start mb-1">
                                <span className="font-medium text-xs text-gray-600">
                                  {isMerchant ? 'MERCHANT' : comment.authorType}
                                </span>
                                <span className="text-xs text-gray-500">{moment(comment.createdAt).format("DD-MMM HH:mm")}</span>
                              </div>
                              <p className="text-sm text-gray-800 whitespace-pre-wrap">{comment.message}</p>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-gray-500 text-sm text-center py-4">No comments yet</p>
                    )}
                    
                    <div className="mt-4 border-t border-gray-100 pt-4">
                      {ticket.status !== 'CLOSED' && ticket.status !== 'RESOLVED' && canUpdateTicket && (
                        <div className="mt-4">
                          {showCommentBox ? (
                            <form onSubmit={handleSubmit(onCommentSubmit)} className="space-y-2">
                              <TextareaField
                                label="Message"
                                name="message"
                                register={register}
                                validation={{ required: "Message is required" }}
                                error={errors.message?.message as string}
                                placeholder="Type your reply here..."
                              />
                              <div className="flex justify-end gap-2">
                                <button 
                                  type="button" 
                                  onClick={() => setShowCommentBox(false)}
                                  className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-200 rounded-lg"
                                >
                                  Cancel
                                </button>
                                <ButtonStyled type="submit" value="Send" isPending={addingComment} />
                              </div>
                            </form>
                          ) : (
                            <button 
                              onClick={() => setShowCommentBox(true)}
                              className="w-full py-2 border border-dashed border-gray-400 text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
                            >
                              + Add Reply
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <div className="space-y-3 max-h-[300px] overflow-y-auto pr-2">
                    {ticket.activityLogs && ticket.activityLogs.length > 0 ? (
                      ticket.activityLogs.map((log: any) => (
                        <div key={log.id} className="flex gap-3 text-sm">
                          <div className="min-w-[120px] text-gray-500 text-xs mt-1">
                            {moment(log.createdAt).format("DD-MMM HH:mm")}
                          </div>
                          <div className="flex-1">
                            <p className="font-medium text-gray-800">
                              {log.description}
                            </p>
                            <p className="text-xs text-gray-500 mt-0.5">
                              By {log.performedByType}
                            </p>
                          </div>
                        </div>
                      ))
                    ) : (
                      <p className="text-gray-500 text-sm text-center py-4">No activity logs yet</p>
                    )}
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="text-center py-4 text-red-500">Failed to load ticket details.</div>
          )}
        </div>

        <div className="border-t border-[#D8D8D8] px-4 py-4 flex justify-end space-x-2 bg-gradient-to-tr from-[#FDF8E5] to-[#FAF8EC] rounded-b-2xl">
          {ticket && canUpdateTicket && (
            <>
              {/* Merchant resolves if it's a customer ticket. Merchant profile type is usually 'lmClient' */}
              {profile?.type === "lmClient" && ticket.createdBy === "CUSTOMER" && ticket.status !== "CLOSED" && ticket.status !== "RESOLVED" && (
                <ButtonStyled
                  value="Resolve Ticket"
                  onClick={() => updateTicket({ ticketId, data: { status: "RESOLVED" } })}
                  isPending={updating}
                  extraClasses="!bg-green-500 hover:!bg-green-600 !text-white"
                />
              )}

              {/* Close/Reopen logic: Owners can close/reopen their own tickets */}
              {ticket.status === "CLOSED" ? (
                <ButtonStyled
                  value="Reopen Ticket"
                  onClick={() => reopenTicket({ ticketId, reason: "Reopened by user" })}
                  isPending={reopening}
                  extraClasses="!bg-blue-500 hover:!bg-blue-600 !text-white"
                />
              ) : (
                <ButtonStyled
                  value="Close Ticket"
                  onClick={() => closeTicket(ticketId)}
                  isPending={closing}
                  extraClasses="!bg-red-500 hover:!bg-red-600 !text-white"
                />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ViewModal;
